import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { MOD } from "@/constants/testIds";
import { Check, X, Sparkles, Loader2, Send, Wand2 } from "lucide-react";
import eventStream from "@/lib/eventStream";

export default function ModerationQueue() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [statusF, setStatusF] = useState("pending");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkRewriteBusy, setBulkRewriteBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // Track the last seen pulse count for the active (siteId, statusF) tuple.
  // When the count changes, the live SSE pulse triggers a list refetch.
  const lastPulseCountRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/sites").then(r => {
      if (cancelled) return;
      setSites(r.data);
      if (r.data.length) setSiteId((prev) => prev || r.data[0].id);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    (async () => {
      const r = await api.get(`/articles?site_id=${siteId}&status=${statusF}&limit=80`);
      if (cancelled) return;
      setItems(r.data.items);
      setSelected((prev) => {
        if (r.data.items.length === 0) return null;
        if (prev && r.data.items.find((x) => x.id === prev.id)) return prev;
        return r.data.items[0];
      });
    })();
    return () => { cancelled = true; };
  }, [siteId, statusF, refreshKey]);

  // Live updates: any time the count for our (siteId, statusF) bucket
  // changes upstream (new ingest, rewrite finished, publish, etc.), the
  // pulse topic emits an updated snapshot and we kick a re-fetch.
  useEffect(() => {
    if (!siteId) return undefined;
    // Reset the pulse tracker whenever the filter changes
    lastPulseCountRef.current = null;
    const unsub = eventStream.subscribe("moderation_pulse", (snap) => {
      const count = snap?.by_site?.[siteId]?.[statusF] ?? 0;
      if (lastPulseCountRef.current === null) {
        lastPulseCountRef.current = count;
        return;
      }
      if (count !== lastPulseCountRef.current) {
        lastPulseCountRef.current = count;
        setRefreshKey((k) => k + 1);
      }
    });
    return unsub;
  }, [siteId, statusF]);

  const reload = () => setRefreshKey((k) => k + 1);

  async function rewrite() {
    if (!selected) return;
    setBusy(true); setMsg("Calling Claude Sonnet 4.6…");
    try {
      const r = await api.post("/articles/rewrite", { article_id: selected.id });
      setSelected(r.data);
      setMsg(`Rewrote in ${r.data.ai_tokens_in}/${r.data.ai_tokens_out} tokens · $${r.data.ai_cost_usd.toFixed(4)}`);
      reload();
    } catch (e) {
      setMsg(`Error: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  }
  async function publish() {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/articles/${selected.id}/publish`);
      setMsg("Published.");
      reload();
    } catch (e) {
      setMsg(`Error: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }
  async function reject() {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/articles/${selected.id}/reject`);
      setMsg("Rejected.");
      reload();
    } finally { setBusy(false); }
  }

  async function bulkPublishReady() {
    if (!siteId) return;
    const readyCount = items.length;
    if (statusF !== "ready") {
      setMsg("Switch to the 'ready' filter first to see what will be published.");
      return;
    }
    if (!window.confirm(`Publish ${readyCount} ready article${readyCount === 1 ? "" : "s"} for this site? Edge cache will be purged automatically.`)) {
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/articles/bulk-publish", { site_id: siteId });
      setMsg(`Published ${r.data.published} article${r.data.published === 1 ? "" : "s"}.`);
      reload();
    } catch (e) {
      setMsg(`Bulk publish error: ${e.response?.data?.detail || e.message}`);
    } finally { setBulkBusy(false); }
  }

  async function bulkRewritePending() {
    if (!siteId) return;
    const pendingCount = items.length;
    if (statusF !== "pending") return;
    if (!window.confirm(
      `Rewrite ${pendingCount} pending article${pendingCount === 1 ? "" : "s"} with AI? This will run in the background.\n\nAI cost ≈ $0.002 per article (Sonnet 4.5). Daily site budget still applies.\n\nAuto-publish (if enabled) will then ship articles that pass the quality gates automatically.`
    )) return;
    setBulkRewriteBusy(true);
    try {
      const r = await api.post("/articles/bulk-rewrite", { site_id: siteId });
      setMsg(`Queued ${r.data.queued} article${r.data.queued === 1 ? "" : "s"} for AI rewrite. Refresh in 1-2 min to see results.`);
      // Reload after a short delay to start showing transitions
      setTimeout(reload, 3000);
    } catch (e) {
      setMsg(`Bulk rewrite error: ${e.response?.data?.detail || e.message}`);
    } finally { setBulkRewriteBusy(false); }
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* Left: list */}
      <div data-testid={MOD.list} className="w-[400px] border-r border-zinc-800 flex flex-col">
        <div className="px-4 py-3 border-b border-zinc-800 space-y-2">
          <select
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm"
          >
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div className="flex gap-1.5">
            {["pending", "ready", "published", "rejected"].map(s => (
              <button
                key={s}
                onClick={() => setStatusF(s)}
                className={`px-2.5 py-1 text-[11px] mono uppercase tracking-wider border rounded-sm ${statusF === s ? "border-white text-white" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}
              >{s}</button>
            ))}
          </div>
          {statusF === "ready" && items.length > 0 && (
            <button
              data-testid="mod-bulk-publish-button"
              onClick={bulkPublishReady}
              disabled={bulkBusy}
              className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-50 px-3 py-1.5 rounded-sm text-xs font-semibold"
            >
              {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Publish all {items.length} ready
            </button>
          )}
          {statusF === "pending" && items.length > 0 && (
            <button
              data-testid="mod-bulk-rewrite-button"
              onClick={bulkRewritePending}
              disabled={bulkRewriteBusy}
              className="w-full inline-flex items-center justify-center gap-1.5 bg-violet-500 text-zinc-950 hover:bg-violet-400 disabled:opacity-50 px-3 py-1.5 rounded-sm text-xs font-semibold"
            >
              {bulkRewriteBusy ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
              AI rewrite all {items.length} pending
            </button>
          )}
        </div>
        <div className="flex-1 overflow-auto divide-y divide-zinc-800">
          {items.length === 0 && <div className="px-4 py-8 text-sm text-zinc-500 text-center">No articles.</div>}
          {items.map(a => (
            <button
              key={a.id}
              data-testid={MOD.item(a.id)}
              onClick={() => setSelected(a)}
              className={`w-full text-left px-4 py-3 hover:bg-zinc-900/40 transition-colors border-l-2 ${selected?.id === a.id ? "border-white bg-zinc-900/60" : "border-transparent"}`}
            >
              <div className="text-sm text-zinc-100 line-clamp-2">{a.title || a.original_title}</div>
              <div className="text-[11px] mono text-zinc-500 mt-1 flex items-center gap-2">
                <span className="dot bg-zinc-600" />
                {a.source_name} · {a.status}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right: detail */}
      <div className="flex-1 overflow-auto bg-zinc-950">
        {!selected ? (
          <div className="h-full flex items-center justify-center text-zinc-500 text-sm">Select an article to review.</div>
        ) : (
          <div className="p-6 max-w-4xl mx-auto">
            <div className="text-xs mono text-zinc-500 mb-2">{selected.source_name} · {selected.status}</div>
            <h2 data-testid={MOD.detailTitle} className="font-display text-2xl font-black tracking-tight">
              {selected.title || selected.original_title}
            </h2>
            {selected.summary && <div className="mt-3 text-zinc-300 text-sm leading-relaxed">{selected.summary}</div>}

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {selected.status === "pending" && (
                <button
                  data-testid={MOD.rewriteButton}
                  disabled={busy}
                  onClick={rewrite}
                  className="inline-flex items-center gap-2 bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50 px-4 py-2 rounded-sm text-sm font-semibold"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Rewrite with AI <span className="kbd ml-1">R</span>
                </button>
              )}
              {selected.status === "ready" && (
                <button
                  data-testid={MOD.publishButton}
                  disabled={busy}
                  onClick={publish}
                  className="inline-flex items-center gap-2 bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-50 px-4 py-2 rounded-sm text-sm font-semibold"
                >
                  <Check size={14} /> Publish <span className="kbd ml-1">A</span>
                </button>
              )}
              {selected.status !== "rejected" && selected.status !== "published" && (
                <button
                  data-testid={MOD.rejectButton}
                  disabled={busy}
                  onClick={reject}
                  className="inline-flex items-center gap-2 border border-zinc-700 hover:border-red-500 hover:text-red-400 text-zinc-300 px-4 py-2 rounded-sm text-sm font-medium"
                >
                  <X size={14} /> Reject
                </button>
              )}
              {msg && <div className="text-xs mono text-zinc-400 ml-2">{msg}</div>}
            </div>

            {selected.body_markdown && (
              <div className="mt-6 border border-zinc-800 rounded-sm p-5 bg-zinc-900/30">
                <div className="label-tiny mb-3">Rewritten body (visible on site)</div>
                <article className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap text-zinc-200 leading-relaxed">
                  {selected.body_markdown}
                </article>
                <div className="mt-4 pt-3 border-t border-zinc-800 text-xs text-zinc-500 mono space-y-1">
                  <div>SEO title: {selected.seo_title}</div>
                  <div>SEO desc: {selected.seo_description}</div>
                  <div>Keywords: {(selected.seo_keywords || []).join(", ")}</div>
                  <div>Cost: ${(selected.ai_cost_usd || 0).toFixed(4)} · {selected.ai_tokens_in}/{selected.ai_tokens_out} tokens</div>
                  <div>Category: {selected.category_slug} · Evergreen: {String(!!selected.is_evergreen)}</div>
                </div>
              </div>
            )}

            <div className="mt-6 border border-zinc-800 rounded-sm p-5 bg-zinc-900/30">
              <div className="label-tiny mb-3">Original source excerpt</div>
              <div className="text-sm text-zinc-400 leading-relaxed">
                {selected.original_body || selected.original_excerpt || "—"}
              </div>
              <div className="mt-3 text-xs mono text-zinc-500">
                <a href={selected.source_url} target="_blank" rel="noreferrer" className="text-amber-500 hover:underline">
                  {selected.source_url}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
